/**
 * AI Summary Accordion Component
 * 
 * Displays the AI-generated executive summary for a school.
 * Used at the top of Geographic, Competitive, and Projections explorers.
 */

import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import { useNavigate } from 'react-router-dom';
import { getStoredSummary } from '../../services/aiService';
import ReactMarkdown from 'react-markdown';

const THEME_COLOR = '#673ab7';

/**
 * AISummaryAccordion - Displays AI executive summary for a school
 * 
 * @param {Object} props
 * @param {Object} props.selectedSchool - The selected school object (must have uuid)
 * @param {number} props.driveTime - Drive time in minutes (5, 10, or 15)
 * @param {string} props.summaryType - Type of summary: 'geographic', 'competitive', or 'projections'
 * @param {boolean} props.defaultExpanded - Whether accordion is expanded by default
 */
const AISummaryAccordion = ({ 
  selectedSchool, 
  driveTime = 10, 
  summaryType = 'geographic',
  travelMode = 'drive',
  defaultExpanded = true,
  isRegenerating = false // Show loading overlay when regenerating
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  
  // Track the current request to prevent race conditions
  const currentRequestRef = React.useRef(null);

  // Load summary when school or parameters change
  useEffect(() => {
    const loadSummary = async () => {
      if (!selectedSchool?.uuid) {
        setSummary(null);
        setLoading(false);
        return;
      }

      // Create a unique request ID
      const requestId = `${selectedSchool.uuid}_${driveTime}_${summaryType}_${travelMode}`;
      currentRequestRef.current = requestId;

      // Clear previous summary immediately when school changes to prevent showing stale data
      setSummary(null);
      setLoading(true);
      setError(null);
      
      const schoolName = selectedSchool.name || selectedSchool.uuid;
      
      try {
        const result = await getStoredSummary(selectedSchool.uuid, driveTime, summaryType, travelMode);
        
        // Check if this request is still current (user didn't switch schools while loading)
        if (currentRequestRef.current !== requestId) {
          return;
        }
        
        if (result.found) {
          setSummary(result);
        } else {
          setSummary(null);
        }
      } catch (err) {
        // Check if this request is still current
        if (currentRequestRef.current !== requestId) {
          return;
        }
        
        console.error('[AISummaryAccordion] Error loading AI summary for', schoolName, ':', err);
        // Silently fail for access errors - component will just not render
        if (err.message && err.message.includes('Access denied')) {
          setSummary(null);
          setError(null);
        } else {
          setError(err.message);
          setSummary(null);
        }
      } finally {
        // Only update loading if this request is still current
        if (currentRequestRef.current === requestId) {
          setLoading(false);
        }
      }
    };

    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool?.uuid, driveTime, summaryType, travelMode]);

  const handleViewFull = () => {
    navigate(`/ai-summary?school=${selectedSchool.uuid}&type=${summaryType}`);
  };

  // Get title based on summary type
  const getTitle = () => {
    switch (summaryType) {
      case 'geographic':
        return 'Executive Summary AI Geographic Insights';
      case 'competitive':
        return 'Executive Summary AI Competitive Insights';
      case 'projections':
        return 'Executive Summary AI Projections Insights';
      default:
        return 'Executive Summary AI Insights';
    }
  };

  // Don't render if no school selected
  if (!selectedSchool?.uuid) {
    return null;
  }

  // Show loading state briefly, but hide completely if no summary after loading
  const isLoadingState = loading;
  const hasContent = !loading && summary;
  const isEmpty = !loading && !summary && !error;

  // Don't render anything while loading OR if no summary found (school doesn't have AI insights)
  // This prevents the brief flash of the accordion before data loads
  // EXCEPTION: If isRegenerating is true, keep the component visible to show the regeneration overlay
  if (isEmpty || isLoadingState) {
    // Only show the loading state if we previously had a summary (updating existing data)
    // OR if we're actively regenerating (even without existing summary)
    // Don't show it on initial load when we don't know if summary exists yet
    if (isLoadingState && !summary && !isRegenerating) {
      return null;
    }
    if (isEmpty && !isRegenerating) {
      return null;
    }
  }

  // Don't render if there's an error (access denied, etc.)
  if (!loading && error && !isRegenerating) {
    return null;
  }

  return (
    <Accordion 
      expanded={expanded}
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      sx={{
        mb: 1.5,
        borderRadius: 1.5,
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: '1px solid',
        borderColor: `${THEME_COLOR}40`,
        backgroundColor: '#f8f5ff',
        overflow: 'hidden',
        '&.Mui-expanded': {
          margin: 0,
          mb: 1.5
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: THEME_COLOR, fontSize: 20 }} />}
        sx={{
          backgroundColor: `${THEME_COLOR}15`,
          minHeight: 40,
          '& .MuiAccordionSummary-content': { my: 0.75 },
          '&.Mui-expanded': {
            minHeight: 40,
            borderBottom: '1px solid',
            borderColor: `${THEME_COLOR}30`
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <AssistantOutlinedIcon sx={{ fontSize: 18, color: THEME_COLOR }} />
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, color: THEME_COLOR }}>
            {getTitle()}
          </Typography>
          {isLoadingState && (
            <CircularProgress size={14} sx={{ color: THEME_COLOR }} />
          )}
          {hasContent && (
            <Chip 
              label="AI Generated" 
              size="small" 
              sx={{ 
                height: 18,
                fontSize: '0.65rem',
                backgroundColor: `${THEME_COLOR}20`, 
                color: THEME_COLOR,
                fontWeight: 500,
                '& .MuiChip-label': { px: 1 }
              }} 
            />
          )}
        </Box>
      </AccordionSummary>
      
      <AccordionDetails sx={{ p: 1.5, pt: 1, pb: 1, backgroundColor: '#f8f5ff', position: 'relative' }}>
        {isRegenerating && !hasContent ? (
          // Show regeneration indicator even if no content exists yet
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1, 
            py: 3,
            minHeight: 100
          }}>
            <CircularProgress size={24} sx={{ color: THEME_COLOR }} />
            <Typography variant="body2" sx={{ color: THEME_COLOR, fontWeight: 500 }}>
              Updating AI insights with new data...
            </Typography>
          </Box>
        ) : isLoadingState ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">Loading AI insights...</Typography>
          </Box>
        ) : hasContent ? (
          <Box>
            {/* Regeneration Overlay */}
            {isRegenerating && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(248, 245, 255, 0.95)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                zIndex: 1,
                borderRadius: 1
              }}>
                <CircularProgress size={24} sx={{ color: THEME_COLOR }} />
                <Typography variant="body2" sx={{ color: THEME_COLOR, fontWeight: 500 }}>
                  Updating AI insights with new data...
                </Typography>
              </Box>
            )}
            
            {/* Executive Summary Content */}
            <Box sx={{ 
              '& p': { mb: 1, lineHeight: 1.5, fontSize: '0.875rem' },
              '& p:last-child': { mb: 0 },
              '& strong': { fontWeight: 600 },
              '& ul': { pl: 2, mb: 1, fontSize: '0.875rem' },
              '& li': { mb: 0.25, lineHeight: 1.4 },
              opacity: isRegenerating ? 0.4 : 1,
              transition: 'opacity 0.3s ease'
            }}>
              <ReactMarkdown>
                {summary.executive_summary || 'No executive summary available.'}
              </ReactMarkdown>
            </Box>

            {/* Footer with AI disclaimer and link */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 1,
              pt: 0.75,
              borderTop: '1px solid',
              borderColor: `${THEME_COLOR}20`,
              opacity: isRegenerating ? 0.4 : 1,
              transition: 'opacity 0.3s ease'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}>
                AI can make mistakes and may miss school-specific details like phasing plans or capacity status. Please verify responses.
              </Typography>
              <Tooltip title="View full AI summary">
                <IconButton 
                  size="small" 
                  onClick={handleViewFull}
                  sx={{ color: THEME_COLOR, p: 0.5 }}
                  disabled={isRegenerating}
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};

export default AISummaryAccordion;

